import { formatDate, formatISO } from 'date-fns';
import AspectsTable from './AspectsTable';
import { CellContext, FilterFnOption } from '@tanstack/react-table';
import { CommonFields } from '../../../lib/types';
import { useState, useEffect } from 'react';

export const authorCell = <T extends CommonFields>(props: CellContext<T, unknown>) => {
  const { updatedAt, updatedBy } = props.row.original;
  const formattedName = extractAndFormatName(updatedBy);

  return (
    <div className={AspectsTable.classes.updatedBy} title={formatISO(updatedAt)} role="cell">
      {formatDate(updatedAt, 'dd/MMM/yyyy')}
      {formattedName && (
        <>
          <br /> by {formattedName}
        </>
      )}
    </div>
  );
};

export const largeTextCell = <T extends CommonFields>(props: CellContext<T, unknown>) => {
  return (
    <div className={AspectsTable.classes.largeTextCell} role="cell">
      {String(props.getValue() ?? '')}
    </div>
  );
};

export const extractAndFormatName = (updatedBy: string | undefined): string => {
  // updatedBy format -> [lastname, firstname](email) OR [firstName lastName](email)

  if (!updatedBy) {
    return '';
  }

  const nameMatch = updatedBy.match(/\[(.*?)\]/);
  if (!nameMatch) {
    return '';
  }
  
  const nameContent = nameMatch[1].trim();
  
  // Check if the format is comma-separated (lastname, firstname)
  if (nameContent.includes(',')) {
    const parts = nameContent.split(',');
    if (parts.length !== 2) {
      return '';
    }
    const firstName = parts[1].trim();
    const lastName = parts[0].trim();
    return `${firstName} ${lastName}`;
  } else {
    // Format is space-separated (FirstName LastName)
    return nameContent;
  }
};

export const defaultSearchFilterFn: FilterFnOption<any> = (
  row,
  _columnId,
  { searchString }: { searchString: string }
) => {
  const { original } = row;

  if (
    // Search string doesn't match
    searchString &&
    !JSON.stringify(original).toLowerCase().includes(searchString.toLowerCase())
  ) {
    return false;
  }

  return true;
};

export const useAlertText = () => {
  const [alertText, setAlertText] = useState('');
  useEffect(() => {
    if (alertText) {
      const delay = process.env.REACT_APP_ALERT_TIMEOUT || '3000';
      const timer = setTimeout(() => setAlertText(''), parseInt(delay));
      return () => clearTimeout(timer);
    }
  }, [alertText]);

  return { alertText, setAlertText };
};
